import { io, Socket } from 'socket.io-client';

export interface PlayerStats {
  id: string;
  username: string;
  email: string;
  wins: number;
  losses: number;
  draws: number;
  elo: number;
  gamesPlayed: number;
  rank?: number;
}

export interface GameRoom {
  id: string;
  name: string;
  hostId: string;
  players: Array<{
    id: string;
    name: string;
    role: string;
    socketId: string;
  }>;
  boardSize: number;
  requireOpenEnd: boolean;
  status: 'WAITING' | 'IN_PROGRESS' | 'FINISHED';
  moves: Array<{
    row: number;
    col: number;
    playerId: string;
    timestamp: number;
  }>;
  createdAt: number;
}

export interface MatchHistory {
  id: string;
  player1Name: string;
  player2Name: string;
  winnerId: string | null;
  moveCount: number;
  duration: number;
  timestamp: number;
  player1EloChange: number;
  player2EloChange: number;
}

export interface OnlinePlayer {
  socketId: string;
  playerId: string; // database id
  username: string;
  inGame: boolean;
}

export class SocketService {
  private socket: Socket | null = null;
  // Backend server URL — point to local backend during development
  // Use full URL including protocol so no prefixing is needed
  // private serverUrl: string = 'https://caro-be-v2-production.up.railway.app';
  private serverUrl: string = 'https://caro.101conceptvn.com';

  // Toggle production behavior. Set to true for production mode.
  // Default to false for local development so we use ws/http.
  public readonly isProduction: boolean = false;

  connect(): void {
    if (this.socket?.connected) return;

    // If serverUrl does not include a protocol, build one based on isProduction
    let endpoint = this.serverUrl;
    if (!/^https?:\/\//.test(endpoint)) {
      endpoint = (this.isProduction ? 'https://' : 'http://') + endpoint;
    }

    // Use socket.io client to connect; prefer 'websocket' transport and secure connection in production
    this.socket = io(endpoint, {
      transports: ['websocket', 'polling'],
      secure: this.isProduction,
    });

    this.socket.on('connect', () => {
      console.info('Connected to server:', this.socket?.id);
    });

    this.socket.on('disconnect', () => {
      console.info('Disconnected from server');
    });

    this.socket.on('error', (data: { message: string }) => {
      console.error('Server error:', data.message);
    });
  }

  disconnect(): void {
    this.socket?.disconnect();
    this.socket = null;
  }

  // Authentication
  register(username: string): Promise<PlayerStats> {
    return new Promise((resolve, reject) => {
      if (!this.socket) {
        reject(new Error('Not connected'));
        return;
      }

      this.socket.emit('register', { username });

      this.socket.once(
        'register-success',
        (data: { player: PlayerStats; deviceToken?: string }) => {
          // Store device token locally for future logins (server returns plaintext token only once)
          if (data.deviceToken) {
            try {
              localStorage.setItem('gomoku_device_token', data.deviceToken);
            } catch (e) {
              /* ignore */
            }
          }
          resolve(data.player);
        }
      );

      this.socket.once('register-error', (data: { message: string }) => {
        reject(new Error(data.message));
      });
    });
  }

  login(username: string): Promise<PlayerStats> {
    return new Promise((resolve, reject) => {
      if (!this.socket) {
        reject(new Error('Not connected'));
        return;
      }

      // Include deviceToken from localStorage if present
      const deviceToken = localStorage.getItem('gomoku_device_token');
      this.socket.emit('login', { username, deviceToken });

      this.socket.once(
        'login-success',
        (data: { player: PlayerStats; verified?: boolean }) => {
          // Optionally store verified flag in local storage for UI, but do not expose token
          try {
            if (typeof data.verified === 'boolean')
              localStorage.setItem(
                'gomoku_verified',
                data.verified ? '1' : '0'
              );
          } catch (e) {}
          resolve(data.player);
        }
      );

      this.socket.once('login-error', (data: { message: string }) => {
        reject(new Error(data.message));
      });
    });
  }

  // Game Management
  createGame(
    playerName: string,
    gameName: string,
    boardSize: number = 30
  ): Promise<GameRoom> {
    return new Promise((resolve, reject) => {
      if (!this.socket) {
        reject(new Error('Not connected'));
        return;
      }
      const onError = (data: { message: string }) => {
        this.socket?.off('game-created');
        reject(new Error(data.message || 'Failed to create game'));
      };

      this.socket.emit('create-game', { playerName, gameName, boardSize });
      this.socket.once(
        'game-created',
        (data: { success: boolean; game: GameRoom }) => {
          this.socket?.off('create-game-error', onError);
          if (data.success) {
            resolve(data.game);
          } else {
            reject(new Error('Failed to create game'));
          }
        }
      );

      // Listen for server-side create-game rejection
      this.socket.on('create-game-error', onError);
    });
  }

  joinGame(gameId: string, playerName: string): Promise<GameRoom> {
    return new Promise((resolve, reject) => {
      if (!this.socket) {
        reject(new Error('Not connected'));
        return;
      }

      this.socket.emit('join-game', { gameId, playerName });

      this.socket.once(
        'game-joined',
        (data: { success: boolean; game: GameRoom }) => {
          if (data.success) {
            resolve(data.game);
          } else {
            reject(new Error('Failed to join game'));
          }
        }
      );
    });
  }

  makeMove(gameId: string, row: number, col: number): void {
    if (!this.socket) return;
    this.socket.emit('make-move', { gameId, row, col });
  }

  endGame(gameId: string, winnerId?: string): void {
    if (!this.socket) return;
    this.socket.emit('end-game', { gameId, winnerId });
  }

  exitToLobby(gameId: string): void {
    if (!this.socket) return;
    this.socket.emit('exit-to-lobby', { gameId });
  }

  getGames(): void {
    if (!this.socket) return;
    this.socket.emit('get-games');
  }

  /**
   * Online players / challenge flow
   */
  getOnlinePlayers(): void {
    if (!this.socket) return;
    this.socket.emit('get-online-players');
  }

  onOnlinePlayers(callback: (players: OnlinePlayer[]) => void): void {
    this.socket?.on('online-players', (data: { players: OnlinePlayer[] }) => {
      callback(data.players);
    });
  }

  challengePlayer(targetSocketId: string): void {
    if (!this.socket) return;
    console.debug &&
      console.debug('[SocketService] challengePlayer ->', targetSocketId);
    this.socket.emit('challenge-player', { targetSocketId });
  }

  onIncomingChallenge(
    callback: (data: {
      requesterSocketId: string;
      requesterDbId: string;
      requesterName: string;
    }) => void
  ): void {
    this.socket?.on('incoming-challenge', (data) => {
      console.debug &&
        console.debug('[SocketService] incoming-challenge received', data);
      try {
        callback(data);
      } catch (err) {
        console.error('onIncomingChallenge callback error', err);
      }
    });
  }

  onChallengeSent(callback: (data: { targetSocketId: string }) => void): void {
    this.socket?.on('challenge-sent', (data) => {
      console.debug && console.debug('[SocketService] challenge-sent', data);
      callback(data);
    });
  }

  acceptChallenge(requesterSocketId: string): void {
    if (!this.socket) return;
    this.socket.emit('accept-challenge', { requesterSocketId });
  }

  declineChallenge(requesterSocketId: string): void {
    if (!this.socket) return;
    this.socket.emit('decline-challenge', { requesterSocketId });
  }

  onChallengeDeclined(
    callback: (data: { requesterSocketId: string }) => void
  ): void {
    this.socket?.on('challenge-declined', callback);
  }

  onChallengeCancelled(
    callback: (data: { requesterSocketId: string }) => void
  ): void {
    this.socket?.on('challenge-cancelled', callback);
  }

  // Leaderboard
  getLeaderboard(limit: number = 100): void {
    if (!this.socket) return;
    this.socket.emit('get-leaderboard', { limit });
  }

  getPlayerStats(playerId: string): void {
    if (!this.socket) return;
    this.socket.emit('get-player-stats', { playerId });
  }

  // Event listeners
  onGameList(callback: (games: GameRoom[]) => void): void {
    this.socket?.on('game-list', (data: { games: GameRoom[] }) => {
      callback(data.games);
    });
  }

  onGameStarted(callback: (game: GameRoom) => void): void {
    this.socket?.on('game-started', (data: { game: GameRoom }) => {
      callback(data.game);
    });
  }

  onMoveMade(
    callback: (data: { gameId: string; move: any; playerId: string }) => void
  ): void {
    this.socket?.on('move-made', callback);
  }

  onGameEnded(
    callback: (data: {
      gameId: string;
      winnerId?: string;
      player1EloChange?: number;
      player2EloChange?: number;
    }) => void
  ): void {
    this.socket?.on('game-ended', callback);
  }

  onPlayerJoined(
    callback: (data: { game: GameRoom; player: any }) => void
  ): void {
    this.socket?.on('player-joined', callback);
  }

  onPlayerLeft(
    callback: (data: { gameId: string; playerId: string }) => void
  ): void {
    this.socket?.on('player-left', callback);
  }

  onOpponentExitedLobby(callback: (data: { gameId: string }) => void): void {
    this.socket?.on('opponent-exited-lobby', callback);
  }

  onLeaderboard(callback: (leaderboard: PlayerStats[]) => void): void {
    this.socket?.on('leaderboard', (data: { leaderboard: PlayerStats[] }) => {
      callback(data.leaderboard);
    });
  }

  onPlayerStatsReceived(
    callback: (data: {
      player: PlayerStats;
      matchHistory: MatchHistory[];
    }) => void
  ): void {
    this.socket?.on('player-stats', callback);
  }

  requestRematch(opponentId: string): void {
    const lastGameResult =
      localStorage.getItem('gomoku_last_game_result') || 'unknown';
    this.socket?.emit('request-rematch', { opponentId, lastGameResult });
  }

  acceptRematch(requesterId: string): void {
    const lastGameResult =
      localStorage.getItem('gomoku_last_game_result') || 'unknown';
    this.socket?.emit('accept-rematch', { requesterId, lastGameResult });
  }

  declineRematch(requesterId: string): void {
    this.socket?.emit('decline-rematch', { requesterId });
  }

  onRematchRequested(
    callback: (data: { requesterId: string; requesterName: string }) => void
  ): void {
    this.socket?.on('rematch-requested', callback);
  }

  onRematchAccepted(callback: (data: { game: GameRoom }) => void): void {
    this.socket?.on('rematch-accepted', callback);
  }

  onRematchDeclined(callback: () => void): void {
    this.socket?.on('rematch-declined', callback);
  }

  onChessClockUpdate(
    callback: (data: {
      gameId: string;
      player1Time: number;
      player2Time: number;
      currentPlayer: 'PLAYER_1' | 'PLAYER_2';
    }) => void
  ): void {
    this.socket?.on('chess-clock-update', callback);
  }

  // Remove event listeners
  off(event: string): void {
    this.socket?.off(event);
  }

  get isConnected(): boolean {
    return this.socket?.connected || false;
  }

  get socketId(): string | undefined {
    return this.socket?.id;
  }
}

export const socketService = new SocketService();
